import React, { useState, useCallback } from 'react';
import {
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  DatePicker,
  Space,
  Button,
  Alert,
  Spin,
  Typography,
  Divider,
  message,
} from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import type {
  CreateCampaignPayload,
  MerchantSearchResult,
} from '../../services/adsService';
import {
  createAdsCampaign,
  fetchMerchantsSearch,
} from '../../services/adsService';

const { Text } = Typography;

interface CreateCampaignModalProps {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}

export const CreateCampaignModal: React.FC<CreateCampaignModalProps> = ({
  open,
  onClose,
  onCreated,
}) => {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Merchant search
  const [merchantQuery, setMerchantQuery] = useState('');
  const [merchants, setMerchants] = useState<MerchantSearchResult[]>([]);
  const [merchantLoading, setMerchantLoading] = useState(false);
  const [selectedMerchant, setSelectedMerchant] =
    useState<MerchantSearchResult | null>(null);

  const handleMerchantSearch = useCallback(async (q: string) => {
    setMerchantQuery(q);
    if (!q.trim()) {
      setMerchants([]);
      return;
    }
    setMerchantLoading(true);
    try {
      const results = await fetchMerchantsSearch(q.trim());
      setMerchants(results);
    } catch {
      setMerchants([]);
      message.error('Không thể tìm merchant. Vui lòng thử lại.');
    } finally {
      setMerchantLoading(false);
    }
  }, []);

  const handleClose = () => {
    form.resetFields();
    setError(null);
    setSelectedMerchant(null);
    setMerchants([]);
    setMerchantQuery('');
    onClose();
  };

  const handleSubmit = async (activateNow: boolean) => {
    try {
      const values = await form.validateFields();
      if (!selectedMerchant) {
        setError('Vui lòng chọn merchant');
        return;
      }
      setSaving(true);
      setError(null);
      const payload: CreateCampaignPayload = {
        merchant_id: selectedMerchant.id,
        type: values.type,
        title: values.title,
        description: values.description || undefined,
        total_budget_points: values.total_budget_points,
        cost_per_success_order_points: values.cost_per_success_order_points,
        daily_budget_points: values.daily_budget_points || null,
        targeting_radius_km: values.targeting_radius_km ?? 3,
        start_at: values.start_at ? values.start_at.toISOString() : null,
        end_at: values.end_at ? values.end_at.toISOString() : null,
        status: activateNow ? 'ACTIVE' : 'DRAFT',
      };
      await createAdsCampaign(payload);
      message.success(
        activateNow
          ? 'Đã tạo và kích hoạt chiến dịch'
          : 'Đã tạo chiến dịch (nháp)'
      );
      handleClose();
      onCreated();
    } catch (e: unknown) {
      const err = e as {
        response?: { data?: { message?: string } };
        message?: string;
      };
      const msg =
        err.response?.data?.message ||
        err.message ||
        'Không thể tạo chiến dịch';
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title="Tạo chiến dịch quảng cáo (Admin)"
      open={open}
      onCancel={handleClose}
      width={600}
      footer={
        <Space>
          <Button onClick={handleClose}>Hủy</Button>
          <Button onClick={() => handleSubmit(false)} loading={saving}>
            Lưu nháp
          </Button>
          <Button
            type="primary"
            onClick={() => handleSubmit(true)}
            loading={saving}
          >
            Tạo & Kích hoạt
          </Button>
        </Space>
      }
    >
      {error && (
        <Alert
          type="error"
          message={error}
          showIcon
          closable
          onClose={() => setError(null)}
          style={{ marginBottom: 16 }}
        />
      )}

      {/* Merchant selector */}
      <div style={{ marginBottom: 16 }}>
        <Text strong>Merchant *</Text>
        <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
          <Input
            prefix={<SearchOutlined />}
            placeholder="Tìm theo tên, ID, SĐT..."
            value={merchantQuery}
            onChange={e => handleMerchantSearch(e.target.value)}
            suffix={merchantLoading ? <Spin size="small" /> : null}
            style={{ flex: 1 }}
          />
        </div>
        {merchants.length > 0 && (
          <div
            style={{
              border: '1px solid #d9d9d9',
              borderRadius: 6,
              maxHeight: 180,
              overflowY: 'auto',
              marginTop: 4,
            }}
          >
            {merchants.map(m => (
              <div
                key={m.id}
                onClick={() => {
                  setSelectedMerchant(m);
                  setMerchants([]);
                  setMerchantQuery(m.name);
                }}
                style={{
                  padding: '8px 12px',
                  cursor: 'pointer',
                  borderBottom: '1px solid #f0f0f0',
                  background:
                    selectedMerchant?.id === m.id ? '#e6f4ff' : '#fff',
                  transition: 'background 0.15s',
                }}
                onMouseEnter={e => {
                  (e.currentTarget as HTMLDivElement).style.background =
                    '#f5f5f5';
                }}
                onMouseLeave={e => {
                  (e.currentTarget as HTMLDivElement).style.background =
                    selectedMerchant?.id === m.id ? '#e6f4ff' : '#fff';
                }}
              >
                <div style={{ fontWeight: 600 }}>{m.name}</div>
                <div style={{ fontSize: 12, color: '#888' }}>
                  ID: {m.id}
                  {m.phone ? ` · ${m.phone}` : ''}
                </div>
              </div>
            ))}
          </div>
        )}
        {selectedMerchant && (
          <div
            style={{
              marginTop: 6,
              padding: '6px 10px',
              background: '#f6ffed',
              border: '1px solid #b7eb8f',
              borderRadius: 4,
              fontSize: 13,
            }}
          >
            Đã chọn: <strong>{selectedMerchant.name}</strong>{' '}
            <Button
              type="link"
              size="small"
              onClick={() => {
                setSelectedMerchant(null);
                setMerchantQuery('');
              }}
            >
              Đổi
            </Button>
          </div>
        )}
      </div>

      <Divider style={{ margin: '12px 0' }} />

      <Form form={form} layout="vertical" size="small">
        <Form.Item
          name="type"
          label="Loại chiến dịch"
          rules={[{ required: true, message: 'Chọn loại' }]}
          initialValue="SHOP"
        >
          <Select
            options={[
              { value: 'SHOP', label: 'Quảng cáo toàn shop' },
              { value: 'ITEM', label: 'Quảng cáo món ăn' },
            ]}
          />
        </Form.Item>

        <Form.Item
          name="title"
          label="Tên chiến dịch"
          rules={[{ required: true, message: 'Nhập tên chiến dịch' }]}
        >
          <Input placeholder="VD: Ưu đãi cuối tuần" />
        </Form.Item>

        <Form.Item name="description" label="Mô tả">
          <Input.TextArea rows={2} placeholder="Mô tả ngắn (không bắt buộc)" />
        </Form.Item>

        <Form.Item
          name="total_budget_points"
          label="Tổng ngân sách (điểm)"
          rules={[
            { required: true, message: 'Nhập ngân sách' },
            { type: 'number', min: 1 },
          ]}
        >
          <InputNumber
            min={1}
            style={{ width: '100%' }}
            placeholder="VD: 1000"
          />
        </Form.Item>

        <Form.Item
          name="cost_per_success_order_points"
          label="CPSO — Điểm mỗi đơn thành công"
          rules={[
            { required: true, message: 'Nhập CPSO' },
            { type: 'number', min: 0, max: 10 },
          ]}
          extra="Tối đa 10 điểm"
        >
          <InputNumber
            min={0}
            max={10}
            style={{ width: '100%' }}
            placeholder="VD: 5"
          />
        </Form.Item>

        <Form.Item
          name="daily_budget_points"
          label="Ngân sách mỗi ngày (điểm, để trống = không giới hạn)"
        >
          <InputNumber
            min={1}
            style={{ width: '100%' }}
            placeholder="Không giới hạn"
          />
        </Form.Item>

        <Form.Item
          name="targeting_radius_km"
          label="Bán kính target (km)"
          initialValue={3}
        >
          <InputNumber
            min={0.5}
            max={10}
            step={0.5}
            style={{ width: '100%' }}
          />
        </Form.Item>

        <Space style={{ width: '100%' }} size="middle">
          <Form.Item
            name="start_at"
            label="Ngày bắt đầu"
            style={{ flex: 1, marginBottom: 0 }}
          >
            <DatePicker
              showTime
              style={{ width: '100%' }}
              format="DD/MM/YYYY HH:mm"
              placeholder="Ngay lập tức"
            />
          </Form.Item>
          <Form.Item
            name="end_at"
            label="Ngày kết thúc"
            style={{ flex: 1, marginBottom: 0 }}
          >
            <DatePicker
              showTime
              style={{ width: '100%' }}
              format="DD/MM/YYYY HH:mm"
              placeholder="Không giới hạn"
            />
          </Form.Item>
        </Space>
      </Form>
    </Modal>
  );
};
